/**
 * searchModel — what the wall's search field and its panel decide, without a DOM (plan
 * `2026_11_01_wall_search_panel.md`, boards 17 and 18): which tokens stand in the field, what the Make row says and
 * whether it is lit, what a tap on a token does, what the panel offers and what the ghost carries.
 * `HeaderSearch` and `SearchPanel` render these answers; `Wall` acts on them.
 */
import {looksLikeRequest, MAX_DIRECTION_CHARS} from '@core/wall/contract'
import {groupValues, type FacetOffer} from '@core/wall/facetOffer'
import {facetToken, type FacetDef, type HouseFacets} from '@core/wall/facets'
import {parseProfileKey} from '@core/wall/profile'
import {composeQuery, parseQuery, samePhrase, type ParsedQuery} from '@core/wall/tsQuery'
import type {TileSummary, WallMakeVia} from '@core/wall/contract'

// ─── Tokens ────────────────────────────────────────────────────────────────────────────────────

/** What a token is in `q` or in the profile: a quoted phrase, a facet token the visitor put in `q`, or a token of the
 *  lean (never in `q`). */
export type TokenRef = {type: 'phrase' | 'facet' | 'lean'; value: string}

export type FieldToken = {
    /** Stable across renders: `type:value`. */
    id: string
    /** `pill` = yours, applied; `lean` = the system's guess, applied. */
    kind: 'pill' | 'lean'
    label: string
    /** A colour value's own colour. */
    swatch?: string
    ref: TokenRef
}

const idOf = (ref: TokenRef) => `${ref.type}:${ref.value}`

/** A facet token's short label, swatch and group name, or `null` when the house has no such value. */
export function facetValueOf(facets: HouseFacets, token: string): {label: string; swatch?: string; group: string} | null {
    const at = token.indexOf(':')
    if (at < 0) return null
    const def = facets.find((f) => f.key === token.slice(0, at))
    const value = def?.values.find((v) => v.id === token.slice(at + 1))
    return def && value ? {label: value.short, swatch: value.swatch, group: def.label} : null
}

function tokenOfFacet(facets: HouseFacets, token: string, type: 'facet' | 'lean'): FieldToken {
    const found = facetValueOf(facets, token)
    const ref: TokenRef = {type, value: token}
    return {
        id: idOf(ref),
        kind: type === 'lean' ? 'lean' : 'pill',
        label: found?.label ?? token.slice(token.indexOf(':') + 1).replace(/-/g, ' '),
        swatch: found?.swatch,
        ref,
    }
}

/** The pills of a parsed query as tokens: the quoted phrases, then the facet tokens, in the order `q` keeps them. */
export function yoursOf(parsed: Pick<ParsedQuery, 'pills' | 'facets'>, facets: HouseFacets): FieldToken[] {
    const phrases = parsed.pills.map((phrase): FieldToken => {
        const ref: TokenRef = {type: 'phrase', value: phrase}
        return {id: idOf(ref), kind: 'pill', label: phrase, ref}
    })
    return [...phrases, ...parsed.facets.map((token) => tokenOfFacet(facets, token, 'facet'))]
}

/** The lean (a profile key, `profile.ts`) as tokens. */
export function leanOf(profile: string, facets: HouseFacets): FieldToken[] {
    return parseProfileKey(profile).map((token) => tokenOfFacet(facets, token, 'lean'))
}

/** The query with the token taken out. A lean token is not in `q`, so it returns `q` unchanged. */
export function withoutToken(q: string, ref: TokenRef): string {
    if (ref.type === 'lean') return q
    const {pills, facets, words} = parseQuery(q)
    return composeWithout(pills, facets, words, ref)
}

function composeWithout(pills: string[], facets: string[], words: string, ref: TokenRef): string {
    return composeQuery(
        ref.type === 'phrase' ? pills.filter((p) => !samePhrase(p, ref.value)) : pills,
        words,
        ref.type === 'facet' ? facets.filter((f) => f !== ref.value) : facets,
    )
}

/** The query with a phrase or a facet token put in, beside the words typed. */
export function withToken(q: string, ref: TokenRef): string {
    const {pills, facets, words} = parseQuery(q)
    if (ref.type === 'phrase') return composeQuery(pills.some((p) => samePhrase(p, ref.value)) ? pills : [...pills, ref.value], words, facets)
    return composeQuery(pills, words, [...facets, ref.value])
}

/** The query with the trailing typed words that a suggestion stands in for replaced by its token (`replaces`). */
export function takeSuggestion(q: string, ref: TokenRef, replaces: number): string {
    const {pills, facets, words} = parseQuery(q)
    const typed = words.trim().split(/\s+/).filter(Boolean)
    const kept = typed.slice(0, Math.max(0, typed.length - Math.max(1, replaces))).join(' ')
    return withToken(composeQuery(pills, kept, facets), ref)
}

// ─── Removing ──────────────────────────────────────────────────────────────────────────────────

/**
 * One click on a token removes it. Yours leaves `q` and says nothing; a lean token is not in `q`, so `q` stays and the
 * token comes back as `dropped`, for the caller to suppress and to announce (`droppedLine`, with Undo).
 */
export function removal(q: string, token: FieldToken): {q: string; dropped: FieldToken | null} {
    if (token.ref.type === 'lean') return {q, dropped: token}
    return {q: withoutToken(q, token.ref), dropped: null}
}

/** The notice that follows a lean token dropped. */
export function droppedLine(token: FieldToken): string {
    return `${token.label} dropped`
}

/** The query a click on a Closest row makes: that phrase as a pill, the request words cleared, the pills and facet
 *  tokens already standing kept. */
export function takeClosest(q: string, term: string): string {
    const {pills, facets} = parseQuery(q)
    return withToken(composeQuery(pills, '', facets), {type: 'phrase', value: term})
}

// ─── The field's track ─────────────────────────────────────────────────────────────────────────

/** Which edges of the field's sideways track fade: the left one only once the track is scrolled, the right one only
 *  while content runs on past it. */
export function trackEdges(scrollLeft: number, clientWidth: number, scrollWidth: number): {left: boolean; right: boolean} {
    return {left: scrollLeft > 0, right: scrollLeft + clientWidth < scrollWidth - 1}
}

// ─── The Make row ──────────────────────────────────────────────────────────────────────────────

export type MakeRow = {
    state: 'empty' | 'typed' | 'pills' | 'request'
    /** Plain text before the bold part, the bold part, the plain text after. */
    lead: string
    strong: string
    tail: string
    /** The key hint at the right; none when the row has no key to give. */
    hint: string | null
    lit: boolean
}

export const MAKE_THIS_PICTURE = 'Make this picture'

/** The ghost's direction, and the Make row's words: the pills' labels and the typed words, comma-joined. */
export function makeDirection(yours: readonly FieldToken[], words: string): string {
    return [...yours.map((t) => t.label), words.trim()].filter(Boolean).join(', ').slice(0, MAX_DIRECTION_CHARS)
}

/**
 * The Make row for what stands in the field. Request length lights it and gives Enter to it; otherwise ⌘/Ctrl+Enter
 * (`apple` picks the glyph) makes. A phone has no hardware key to name and a narrow row: no hint and no trailing
 * words there.
 */
export function makeRow(
    yours: readonly FieldToken[], words: string, opts: {apple: boolean; phone: boolean},
): MakeRow {
    const typed = words.trim()
    const key = opts.apple ? '⌘ ⏎' : 'Ctrl ⏎'
    if (looksLikeRequest(typed)) {
        return {state: 'request', lead: '', strong: MAKE_THIS_PICTURE, tail: '', hint: opts.phone ? null : '⏎ enter', lit: true}
    }
    if (yours.length > 0) {
        return {state: 'pills', lead: 'Make ', strong: makeDirection(yours, typed), tail: '', hint: opts.phone ? null : key, lit: false}
    }
    if (typed) {
        return {
            state: 'typed', lead: 'Make ', strong: `“${typed}”`, tail: opts.phone ? '' : ' — open the ghost with it',
            hint: opts.phone ? null : key, lit: false,
        }
    }
    return {
        state: 'empty', lead: 'Describe a picture — ', strong: opts.phone ? 'eight words' : 'eight words and Enter',
        tail: ' make it', hint: null, lit: false,
    }
}

/** Whether the Make row can make anything: the empty row is a sentence, not a button. */
export function makeIsAvailable(row: MakeRow): boolean {
    return row.state !== 'empty'
}

/** What a key does at the field, in the order the rules apply: ⌘/Ctrl+Enter makes at any length, Enter takes the
 *  current row when there is one, else makes at request length, and otherwise only blurs. */
export type EnterAction = 'make' | 'take' | 'blur'

export function enterAction(
    event: {metaKey: boolean; ctrlKey: boolean}, row: MakeRow, hasCurrentRow: boolean,
): EnterAction {
    if ((event.metaKey || event.ctrlKey) && makeIsAvailable(row)) return 'make'
    if (hasCurrentRow) return 'take'
    return row.state === 'request' ? 'make' : 'blur'
}

/** How a make was asked for (`wall_tile_armed.via`). */
export type MakeVia = WallMakeVia

export function viaOf(event: {metaKey: boolean; ctrlKey: boolean}): MakeVia {
    return event.metaKey || event.ctrlKey ? 'cmd_enter' : 'enter'
}

// ─── The return key and the lean ───────────────────────────────────────────────────────────────

/** `enterkeyhint` of the field: `go` while the Make row is lit, `search` otherwise. */
export function enterKeyHint(row: MakeRow): 'search' | 'go' {
    return row.lit ? 'go' : 'search'
}

/** A request is a whole picture, the lean does not enter it: the lean steps out of the field while the Make row is lit. */
export function leanShown(lean: readonly FieldToken[], row: MakeRow): FieldToken[] {
    return row.lit ? [] : [...lean]
}

// ─── What the panel offers ─────────────────────────────────────────────────────────────────────

export const CHIP_LINES = 2

/**
 * How many of a group's chips stand when the group is held to `lines` lines. `widths` are the chips' own widths in
 * order, `room` the width of the line, `gap` the space between chips, `moreWidth` the width of the `+N` chip that
 * takes the place of the rest. When all the chips fit in `lines` lines, all of them stand and there is no `+N`.
 */
export function visibleChips(widths: readonly number[], room: number, gap: number, moreWidth: number, lines = CHIP_LINES): number {
    const linesFor = (ws: readonly number[]) => {
        let used = 1
        let x = 0
        for (const w of ws) {
            if (x > 0 && x + gap + w > room) {
                used += 1
                x = w
            } else {
                x += (x > 0 ? gap : 0) + w
            }
        }
        return used
    }
    if (linesFor(widths) <= lines) return widths.length
    for (let k = widths.length - 1; k > 0; k--) {
        if (linesFor([...widths.slice(0, k), moreWidth]) <= lines) return k
    }
    return 1
}

export type OfferedGroup = {key: string; label: string; values: {token: string; label: string; swatch?: string}[]}

/**
 * The facet groups the empty panel offers: per facet decided by the pack, the values `groupValues` picks from the
 * group's `offer` (the server's ranking for this visitor and day; without one, the values with the most packs, from
 * `shares`, the house's `facetShares.json`). A value in `taken` (in the query or shown as lean) is not offered again.
 * A facet decided by the cell (Toile's style) is offered under typing, not as a group.
 */
export function offeredGroups(
    facets: HouseFacets, shares: Record<string, number>, taken: readonly string[], offer: FacetOffer = {},
): OfferedGroup[] {
    const groups: OfferedGroup[] = []
    for (const def of facets as readonly FacetDef[]) {
        if (def.level === 'tile') continue
        const values = groupValues(def, offer[def.key], shares, taken)
            .flatMap((id) => def.values.filter((v) => v.id === id))
            .map((v) => ({token: facetToken(def.key, v.id), label: v.short, swatch: v.swatch}))
        if (values.length > 0) groups.push({key: def.key, label: def.label, values})
    }
    return groups
}

/**
 * How many of the loaded tiles answer the query. The seam (`useWallPages.seamAt`) is only known once the matched
 * segment is exhausted, which a query with more matches than one page reaches on a later page: until then every
 * loaded tile of a non-empty query is a match, and none is when there is no query.
 */
export function matchedOnScreen(loaded: number, seamAt: number | null, loadedQuery: string): number {
    if (seamAt !== null) return seamAt
    return loadedQuery.trim() ? loaded : 0
}

export const MAX_WITH_THESE = 4

/**
 * With these: the chips of the matched tiles on screen (the first `matched` of `tiles`), most carried first, minus
 * the phrases already in the field. No request: the tiles are the ones the wall already holds.
 */
export function withThese(tiles: readonly TileSummary[], matched: number, pills: readonly string[]): string[] {
    const counts = new Map<string, {label: string; n: number}>()
    for (const tile of tiles.slice(0, Math.max(0, matched))) {
        for (const chip of tile.chips) {
            if (pills.some((p) => samePhrase(p, chip))) continue
            const key = chip.toLowerCase()
            const seen = counts.get(key)
            if (seen) seen.n += 1
            else counts.set(key, {label: chip, n: 1})
        }
    }
    // Map iterates in insertion order and sort is stable: ties keep the wall's own order.
    return [...counts.values()].sort((a, b) => b.n - a.n).slice(0, MAX_WITH_THESE).map((c) => c.label)
}

/** The With section's name: the one thing yours when there is one, otherwise these. */
export function withHeading(yours: readonly FieldToken[]): string {
    return yours.length === 1 ? `With ${yours[0].label}` : 'With these'
}

/** The spans of `label` whose words begin with a typed word (the phrase rows' bold). */
export function typedSpans(label: string, typedWords: string): [number, number][] {
    const typed = typedWords.toLowerCase().split(/\s+/).filter((w) => w.length > 0)
    const spans: [number, number][] = []
    for (const m of label.matchAll(/[\p{L}\p{N}]+/gu)) {
        const word = m[0].toLowerCase()
        const hit = typed.find((t) => word.startsWith(t))
        if (hit) spans.push([m.index, m.index + Math.min(word.length, hit.length)])
    }
    return spans
}

/** What the panel shows under the field: the closest pictures at request length, suggestions under typed words, what
 *  sits with the pills when only pills stand, and otherwise the sections of the empty panel. */
export type PanelBody = 'closest' | 'rows' | 'with' | 'rest'

export function panelBody(row: MakeRow, words: string, yoursCount: number): PanelBody {
    if (row.lit) return 'closest'
    if (words.trim()) return 'rows'
    return yoursCount > 0 ? 'with' : 'rest'
}

/** Whether a swatch would sink into a dark surface and so wears a ring: a dark colour (black, brown), or a grey that
 *  is not light. */
export function darkSwatch(hex: string): boolean {
    const m = /^#([0-9a-f]{6})$/i.exec(hex)
    if (!m) return false
    const n = parseInt(m[1], 16)
    const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    const [r, g, b] = channels.map((c) => {
        const v = c / 255
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
    })
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
    const grey = Math.max(...channels) - Math.min(...channels) < 40
    return luminance < 0.14 || (grey && luminance < 0.3)
}

// ─── The phone's half sheet ────────────────────────────────────────────────────────────────────

export const DRAG_FLOOR = 48

/** A drag on the grab handle: down closes the half sheet, up opens the full panel, a short drag does neither. */
export function dragOutcome(dy: number): 'close' | 'expand' | 'stay' {
    if (dy >= DRAG_FLOOR) return 'close'
    if (dy <= -DRAG_FLOOR) return 'expand'
    return 'stay'
}
